/**
 * Dashboard-only sport keys — board subsets that are NOT sports.
 *
 * Two live cases: the ATP Challenger circuit (#604) and the CFB FBS/FCS
 * subdivisions (#653). Both split one authority sport's board without adding a
 * sport: the row keeps its own `sport`, and that sport stays the only
 * permission, limit, credential, and order-routing authority. These keys exist
 * purely for dashboard tabs and board URLs — they must never enter the shared
 * Sport union, manager grants, or order payloads.
 *
 * ── ATP Challenger (#604) ─────────────────────────────────────────────
 *
 * Kalshi gives the Challenger circuit its own durable series
 * (KXATPCHALLENGERMATCH), so classification is by EXACT first ticker
 * segment. The ticker-parser boundary is prefix-based by design; exactness
 * lives here so a sibling or superstring series can never leak between
 * circuits.
 *
 * ── CFB subdivisions (#653) ───────────────────────────────────────────
 *
 * Hydra publishes no subdivision on CFB frames, so classification comes from
 * the verified team table in `lib/cfb/subdivision` (see that module for why).
 * Plain `'cfb'` deliberately keeps returning the WHOLE slate: the default board
 * is unchanged, and the buckets refine it. A cross-subdivision game matches
 * both `cfb-fbs` and `cfb-fcs`; an unclassified one matches `cfb-other` and
 * still shows under `cfb`.
 */
import { SPORT_TABS, type Sport } from '../../views/ValueDashboard/types';
import type { CfbBucket, CfbClassifiableRow } from '../cfb/subdivision';
import { cfbRowMatchesBucket } from '../cfb/subdivision';

export const ATP_CHALLENGER_DASHBOARD_SPORT = 'atp-challenger' as const;
export const ATP_CHALLENGER_SERIES = 'KXATPCHALLENGERMATCH' as const;

/** CFB subdivision board keys. Ordered as the toolbar renders them. */
export const CFB_BUCKET_DASHBOARD_SPORTS = ['cfb-fbs', 'cfb-fcs', 'cfb-other'] as const;
export type CfbBucketDashboardSport = (typeof CFB_BUCKET_DASHBOARD_SPORTS)[number];

/** A Sport, or a dashboard-only board key (Challenger circuit, CFB bucket). */
export type DashboardSport =
  | Sport
  | typeof ATP_CHALLENGER_DASHBOARD_SPORT
  | CfbBucketDashboardSport;

/** The CFB bucket a dashboard key names, or null when it names something else
 *  — including plain `'cfb'`, which is the whole slate, not a bucket. */
export function cfbBucketOf(sport: DashboardSport): CfbBucket | null {
  switch (sport) {
    case 'cfb-fbs':
      return 'fbs';
    case 'cfb-fcs':
      return 'fcs';
    case 'cfb-other':
      return 'other';
    default:
      return null;
  }
}

/** The board a `?sport=` value names, or null when it names none — an unknown
 *  string, or the 'all' aggregate the caller handles separately. Parsing lives
 *  here so the set of legal keys is stated exactly once, beside the filter that
 *  gives them meaning. */
export function dashboardSportFromParam(param: string | null | undefined): DashboardSport | null {
  if (!param) return null;
  if (param === ATP_CHALLENGER_DASHBOARD_SPORT) return ATP_CHALLENGER_DASHBOARD_SPORT;
  if ((CFB_BUCKET_DASHBOARD_SPORTS as readonly string[]).includes(param)) {
    return param as CfbBucketDashboardSport;
  }
  return SPORT_TABS.some((tab) => tab.id === param) ? (param as Sport) : null;
}

/** The minimal row surface the classifier needs — structurally satisfied by
 *  GameRow and by focusedDashboard's FocusableGameRow (whose sport is a plain
 *  string) without importing either full shape. */
export interface CircuitClassifiableRow extends CfbClassifiableRow {
  kalshi?: {
    away: { marketTicker: string };
    home: { marketTicker: string };
  } | null;
}

/** Exact first dash-segment of a Kalshi ticker, or null when empty. */
export function kalshiSeriesFromTicker(ticker: string): string | null {
  const segment = ticker.split('-', 1)[0];
  return segment ? segment : null;
}

export function isAtpChallengerTicker(ticker: string): boolean {
  return kalshiSeriesFromTicker(ticker) === ATP_CHALLENGER_SERIES;
}

/** A row is Challenger iff either Kalshi book carries the exact series.
 *  No books ⇒ not Challenger (fail toward the main-tour board). */
export function isAtpChallengerRow(row: CircuitClassifiableRow): boolean {
  if (row.sport !== 'atp' || !row.kalshi) return false;
  return (
    isAtpChallengerTicker(row.kalshi.away.marketTicker) ||
    isAtpChallengerTicker(row.kalshi.home.marketTicker)
  );
}

/** Board-key selection filter. 'atp' excludes exact Challenger rows;
 *  'atp-challenger' includes only them; a CFB bucket key narrows the CFB
 *  slate while plain 'cfb' keeps all of it; every other sport is identity.
 *  The 'all sports' path never calls this — the aggregate board must keep
 *  both circuits and the whole CFB slate. */
export function dashboardSportMatches(
  row: CircuitClassifiableRow,
  sport: DashboardSport,
): boolean {
  if (sport === ATP_CHALLENGER_DASHBOARD_SPORT) return isAtpChallengerRow(row);
  if (sport === 'atp') return row.sport === 'atp' && !isAtpChallengerRow(row);
  const bucket = cfbBucketOf(sport);
  if (bucket) return cfbRowMatchesBucket(row, bucket);
  return row.sport === sport;
}

/** The authority sport behind a dashboard selection — settings, getGames,
 *  and every shared sport API take this, never the dashboard key. */
export function underlyingSport(sport: DashboardSport): Sport {
  if (sport === ATP_CHALLENGER_DASHBOARD_SPORT) return 'atp';
  return cfbBucketOf(sport) ? 'cfb' : (sport as Sport);
}
